/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ConfirmDialogComponent, NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EXPIRING_DAYS, ShareLinkRow, ShareLinkState, ShareLinksService } from './share-links.service';

type StateFilter = 'all' | ShareLinkState;

/**
 * Share links: every public link to a document, in one place.
 *
 * A link made from the share dialog used to be visible only from its document,
 * so nobody could answer "what have we sent outside, and is any of it still
 * open". This lists the links, flags the ones without an expiry or about to
 * expire, shows how often each was opened, and revokes one in a click.
 */
@Component({
  selector: 'app-share-links-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './share-links-page.component.html',
  styleUrls: ['./share-links-page.component.scss']
})
export class ShareLinksPageComponent implements OnInit {
  private readonly links = inject(ShareLinksService);
  private readonly translate = inject(TranslateService);
  private readonly notification = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  readonly expiringDays = EXPIRING_DAYS;

  readonly rows = signal<ShareLinkRow[]>([]);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly truncated = signal(false);
  /** False when the jobs sidecar could not be asked; the counts column then says so. */
  readonly opensKnown = signal(true);
  readonly revoking = signal<string | null>(null);

  readonly scope = signal<'mine' | 'all'>('mine');
  readonly stateFilter = signal<StateFilter>('all');
  readonly query = signal('');

  readonly visible = computed(() => {
    const words = this.query().trim().toLowerCase();
    const state = this.stateFilter();
    return this.rows().filter(
      (row) =>
        (state === 'all' || row.state === state) && (!words || row.name.toLowerCase().includes(words) || row.sharedBy.toLowerCase().includes(words))
    );
  });

  readonly counts = computed(() => {
    const rows = this.rows();
    return {
      total: rows.length,
      none: rows.filter((row) => row.state === 'none').length,
      soon: rows.filter((row) => row.state === 'soon' || row.state === 'expired').length,
      opens: rows.reduce((sum, row) => sum + (row.opens ?? 0), 0)
    };
  });

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.failed.set('');
    this.links.list(this.scope() === 'mine').subscribe({
      next: ({ rows, truncated }) => {
        this.rows.set(rows);
        this.truncated.set(truncated);
        this.loading.set(false);
        this.loadOpens(rows);
      },
      error: (err) => {
        this.loading.set(false);
        this.rows.set([]);
        this.failed.set(this.describe(err));
      }
    });
  }

  private loadOpens(rows: ShareLinkRow[]): void {
    this.links.opens(rows.map((row) => row.id)).subscribe((opens) => {
      this.opensKnown.set(opens !== null);
      if (!opens) {
        return;
      }
      this.rows.update((list) => list.map((row) => ({ ...row, opens: opens[row.id]?.count ?? 0, lastOpenedAt: opens[row.id]?.lastAt || undefined })));
    });
  }

  setScope(scope: 'mine' | 'all'): void {
    if (scope !== this.scope()) {
      this.scope.set(scope);
      this.reload();
    }
  }

  // ---- actions --------------------------------------------------------------

  openDocument(row: ShareLinkRow): void {
    void this.router.navigate(['/document', row.nodeId]);
  }

  urlOf(row: ShareLinkRow): string {
    return this.links.urlOf(row.id);
  }

  async copy(row: ShareLinkRow): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.links.urlOf(row.id));
      this.notification.showInfo(this.translate.instant('DMS.SHARELINKS.COPIED'));
    } catch {
      this.notification.showError(this.translate.instant('DMS.SHARELINKS.COPY_FAILED'));
    }
  }

  /** Ends a link after asking: anyone holding the URL loses access at once. */
  revoke(row: ShareLinkRow): void {
    if (this.revoking()) {
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'DMS.SHARELINKS.REVOKE_TITLE',
          message: this.translate.instant('DMS.SHARELINKS.REVOKE_CONFIRM', { name: row.name }),
          yesLabel: 'DMS.SHARELINKS.REVOKE',
          noLabel: 'DMS.UI.7D3094'
        },
        minWidth: '320px',
        restoreFocus: true
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed !== true) {
          return;
        }
        this.revoking.set(row.id);
        this.links.revoke(row.id).subscribe({
          next: () => {
            this.revoking.set(null);
            this.rows.update((list) => list.filter((entry) => entry.id !== row.id));
            this.notification.showInfo(this.translate.instant('DMS.SHARELINKS.REVOKED', { name: row.name }));
          },
          error: (err) => {
            this.revoking.set(null);
            const key = err?.status === 403 ? 'DMS.SHARELINKS.REVOKE_DENIED' : 'DMS.SHARELINKS.REVOKE_FAILED';
            this.notification.showError(this.translate.instant(key, { name: row.name }));
          }
        });
      });
  }

  // ---- view helpers ---------------------------------------------------------

  stateKey(row: ShareLinkRow): string {
    return 'DMS.SHARELINKS.STATE_' + row.state.toUpperCase();
  }

  /** Whole days until the link expires; 0 on its last day. */
  daysLeft(row: ShareLinkRow): number {
    return row.expiresAt ? Math.max(0, Math.floor((new Date(row.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000))) : 0;
  }

  trackById(_: number, row: ShareLinkRow): string {
    return row.id;
  }

  private describe(error: any): string {
    return error?.error?.error?.briefSummary || error?.message || this.translate.instant('DMS.SHARELINKS.LOAD_FAILED');
  }
}
